import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { STORE_KEYS } from "@/other/constants/StoreKeys.jsx";
import { boardContextApi, uploadFile } from "@/network/network.jsx";
import { showToast } from "@/other/utils/utils.jsx";
import type { BoardContext, DraftQuestion } from "@/bryntum-lib/models/BoardContext";
import type { BoardContextGroup } from "@/bryntum-lib/models/BoardContextGroup";
import type { BoardContextStatement } from "@/bryntum-lib/models/BoardContextStatement";
import { ASYNC_TASK_TYPES } from "@/other/constants/OtherKeys.jsx";
import { useAsyncTaskContext } from "@/modern/components/async-tasks/context/AsyncTaskContext";
import {
  matchBoardContextBoardTask,
  matchBoardContextTask,
} from "@/modern/components/async-tasks/utils";
import type { AsyncTaskTypeId } from "@/modern/components/async-tasks/types";
import { useContextViewStep, type ContextViewStep } from "./useContextViewState";
import { useHasOpenAIKey } from "@/react/hooks/useHasOpenAIKey";
import { useAsyncTaskFailureReporter, useIntegrationErrorReporter } from "@/integrations";

// ── Types ───────────────────────────────────────────────────────────────────

export interface CrawledPage {
  url: string;
  title: string;
  description: string;
}

export type ModalMode = "create" | "resume" | "extend";

interface ContextFormContextValue {
  contextId: number | null;
  boardContextRecord: BoardContext | null;
  groups: BoardContextGroup[];
  statements: BoardContextStatement[];
  draftQuestions: DraftQuestion[];

  /** Resolved current wizard step (async task phase + back-nav override support). */
  currentStep: ContextViewStep | null;

  /** True when context is completed and has non-preview groups. */
  hasContextView: boolean;
  /** True when the context record has draft questions ready. */
  hasDraftQuestions: boolean;

  // Setup
  isLoadingMapUrl: boolean;
  crawledPages: CrawledPage[];
  handleSetupSubmit: (data: {
    name: string;
    websiteUrl: string;
    businessDescription: string;
    file: File | null;
    fileName: string;
  }) => Promise<void>;

  // Page picker
  selectedUrls: string[];
  setSelectedUrls: (urls: string[]) => void;
  handleAnalysePages: () => Promise<void>;
  isSubmittingPagePick: boolean;
  canContinueToQuestions: boolean;
  handleContinueToQuestions: () => void;

  // Questions
  answers: Record<number, string[]>;
  setAnswer: (index: number, answer: string[]) => void;
  handleBuildContext: (withoutAnswers?: boolean) => Promise<void>;
  isSubmittingAnswers: boolean;

  // Suggestions
  isGeneratingSuggestions: boolean;
  handleGenerateSuggestions: () => Promise<void>;
  handleDuplicateAndGenerate: () => Promise<void>;

  // Navigation helpers
  handleBackFromPagePicker: () => void;
  handleBackFromQuestions: () => void;

  // Draft management
  canCancelDraft: boolean;
  cancelDraft: () => void;

  hasOpenAIKey: boolean;

  /** True when this provider is wrapping the read-only panel (no wizard state). */
  panelMode: boolean;
}

const ContextFormCtx = createContext<ContextFormContextValue | undefined>(undefined);

// ── Constants ───────────────────────────────────────────────────────────────

const DEFAULT_PAGE_PICKER_SELECTION = 8;

const ASYNC_TASK_WIZARD_STEPS: ContextViewStep[] = ["analysing", "building", "suggesting"];

const defaultSelectedUrls = (pages: CrawledPage[]) =>
  pages.slice(0, DEFAULT_PAGE_PICKER_SELECTION).map((p) => p.url);

const selectedUrlsEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((url) => setB.has(url));
};

// ── Provider ────────────────────────────────────────────────────────────────

interface ContextFormProviderProps {
  children: ReactNode;
  contextId?: number | null;
  draftNewContext?: boolean;
  mode?: ModalMode;
  panelMode?: boolean;
  onContextCreated?: (id: number) => void;
  onDraftCancel?: () => void;
  duplicateContextForExtend?: (sourceId: number) => Promise<number>;
  finalizeExtendDuplicate?: (newId: number) => void;
}

export const ContextFormProvider = ({
  children,
  contextId: propContextId,
  draftNewContext = false,
  mode = "resume",
  panelMode = false,
  onContextCreated,
  onDraftCancel,
  duplicateContextForExtend,
  finalizeExtendDuplicate,
}: ContextFormProviderProps) => {
  const { project, selectedBoard } = useSwivuBryntumContext();
  const slug = useMemo(
    () => (selectedBoard?.record as any)?.slug as string | undefined,
    [selectedBoard]
  );
  const boardId = useMemo(() => selectedBoard?.id, [selectedBoard]);
  const api = useMemo(() => (slug ? boardContextApi(slug) : null), [slug]);
  const { pinTask } = useAsyncTaskContext();
  const { reportError } = useIntegrationErrorReporter();

  const [resolvedContextId, setResolvedContextId] = useState<number | null>(
    draftNewContext ? null : (propContextId ?? null)
  );

  useEffect(() => {
    if (!draftNewContext && propContextId != null) {
      setResolvedContextId(propContextId);
    }
  }, [propContextId, draftNewContext]);

  // For non-draft flows (resume/extend), propContextId is always authoritative — use it
  // directly to avoid a one-render lag when finalizeExtendDuplicate updates the parent.
  // For draft creation, resolvedContextId is set when the new record is created (propContextId is null until then).
  const effectiveContextId = (!draftNewContext && propContextId != null)
    ? propContextId
    : (resolvedContextId ?? null);

  const pinContextTask = useCallback(
    (contextId: number, type: AsyncTaskTypeId, taskId: string) => {
      if (boardId == null) return;
      pinTask(Number(taskId), {
        type,
        meta_data: {
          board_id: boardId,
          board_context_id: contextId,
        },
      });
    },
    [boardId, pinTask],
  );

  const matchContextTask = useCallback(
    (task: Parameters<typeof matchBoardContextTask>[0]) =>
      boardId != null &&
      effectiveContextId != null &&
      matchBoardContextBoardTask(task, boardId) &&
      matchBoardContextTask(task, boardId, effectiveContextId),
    [boardId, effectiveContextId],
  );

  useAsyncTaskFailureReporter({
    matchTask: matchContextTask,
    provider: "openai",
    fallback: "Board context AI task failed.",
  });

  // Store subscriptions
  const { version: boardContextVersion } = useSwivuStore({ storeId: STORE_KEYS.boardContextStore });
  const { version: boardContextGroupVersion } = useSwivuStore({ storeId: STORE_KEYS.boardContextGroupStore });
  const { version: boardContextStatementVersion } = useSwivuStore({ storeId: STORE_KEYS.boardContextStatementStore });

  const hasOpenAIKey = useHasOpenAIKey();

  // Derived record data
  const boardContextRecord = useMemo<BoardContext | null>(() => {
    if (!project || effectiveContextId == null) return null;
    void boardContextVersion;
    const store = project.getCrudStore(STORE_KEYS.boardContextStore);
    return (store?.getById(effectiveContextId) as unknown as BoardContext) ?? null;
  }, [project, effectiveContextId, boardContextVersion]);

  const getContextRecord = useCallback((): BoardContext | null => {
    if (!project || effectiveContextId == null) return null;
    const store = project.getCrudStore(STORE_KEYS.boardContextStore);
    return (store?.getById(effectiveContextId) as unknown as BoardContext) ?? null;
  }, [project, effectiveContextId]);

  const groups = useMemo<BoardContextGroup[]>(() => {
    if (!project || !boardContextRecord) return [];
    void boardContextGroupVersion;
    const store = project.getCrudStore(STORE_KEYS.boardContextGroupStore);
    if (!store) return [];
    return (store.records as unknown as BoardContextGroup[])
      .filter((g) => g.boardContext === boardContextRecord.id)
      .sort((a, b) => a.order - b.order);
  }, [project, boardContextRecord, boardContextGroupVersion]);

  const statements = useMemo<BoardContextStatement[]>(() => {
    if (!project || groups.length === 0) return [];
    void boardContextStatementVersion;
    const store = project.getCrudStore(STORE_KEYS.boardContextStatementStore);
    if (!store) return [];
    const groupIds = new Set(groups.map((g) => g.id));
    return (store.records as unknown as BoardContextStatement[])
      .filter((s) => groupIds.has(s.group))
      .sort((a, b) => a.order - b.order);
  }, [project, groups, boardContextStatementVersion]);

  const draftQuestions = useMemo<DraftQuestion[]>(
    () => boardContextRecord?.draftQuestions?.questions ?? [],
    [boardContextRecord, boardContextVersion]
  );

  // ── Step resolution ────────────────────────────────────────────────────────

  /**
   * Local override for back-navigation (e.g. "Back" from QuestionsStep → page-picker).
   * Cleared immediately when a new async task is submitted.
   */
  const [stepOverride, setStepOverride] = useState<ContextViewStep | null>(null);

  const derivedStep = useContextViewStep(effectiveContextId);

  const currentStep = useMemo(() => {
    if (derivedStep == null) return null;
    if (ASYNC_TASK_WIZARD_STEPS.includes(derivedStep)) return derivedStep;
    return stepOverride ?? derivedStep;
  }, [derivedStep, stepOverride]);

  // Computed booleans
  const hasDraftQuestionsFlag = useMemo(
    () =>
      boardContextRecord?.draftQuestions !== null &&
      Array.isArray(boardContextRecord?.draftQuestions?.questions) &&
      (boardContextRecord?.draftQuestions?.questions.length ?? 0) > 0,
    [boardContextRecord, boardContextVersion]
  );

  const hasContextView = useMemo(
    () =>
      boardContextRecord?.status === "completed" &&
      groups.some((g) => !g.isPreview),
    [boardContextRecord, groups]
  );

  // ── Form state ────────────────────────────────────────────────────────────

  const [isLoadingMapUrl, setIsLoadingMapUrl] = useState(false);
  const [crawledPages, setCrawledPages] = useState<CrawledPage[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const [answers, setAnswers] = useState<Record<number, string[]>>({});
  const [isSubmittingPagePick, setIsSubmittingPagePick] = useState(false);
  const [isSubmittingAnswers, setIsSubmittingAnswers] = useState(false);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);

  const setAnswer = useCallback((index: number, answer: string[]) => {
    setAnswers((prev) => ({ ...prev, [index]: answer }));
  }, []);

  // ── API actions ───────────────────────────────────────────────────────────

  const triggerGenerateQuestions = useCallback(
    async (contextId?: number) => {
      const record =
        contextId != null && project
          ? ((project.getCrudStore(STORE_KEYS.boardContextStore)?.getById(contextId) as unknown as BoardContext) ?? null)
          : getContextRecord();
      if (!api || !record?.id) return;

      setStepOverride(null);
      try {
        const res = await api.generateQuestions(record.id);
        const taskId = String(res.data.id);
        pinContextTask(
          record.id,
          ASYNC_TASK_TYPES.BOARD_CONTEXT_QUESTIONS_GENERATION,
          taskId,
        );
      } catch (error) {
        reportError({
          error,
          provider: "openai",
          fallback: "Failed to start question generation.",
        });
      }
    },
    [api, project, getContextRecord, pinContextTask, reportError]
  );

  const handleSetupSubmit = useCallback(
    async ({
      name,
      websiteUrl,
      businessDescription,
      file,
      fileName,
    }: {
      name: string;
      websiteUrl: string;
      businessDescription: string;
      file: File | null;
      fileName: string;
    }) => {
      if (!project || !api) return;
      const store = project.getCrudStore(STORE_KEYS.boardContextStore);
      if (!store) return;

      let privateDocumentId: number | null = null;
      if (file) {
        try {
          const uploadRes = await uploadFile(file, fileName);
          privateDocumentId = uploadRes?.data?.id ?? null;
        } catch {
          showToast({ msg: "Failed to upload document. Please try again.", type: "error", duration: 4000 });
          return;
        }
      }

      let existing = getContextRecord();
      if (!existing && draftNewContext && boardId) {
        const newRecord = store.add({
          board: boardId,
          name: name.trim() || null,
          isActive: false,
        });
        try {
          await project.sync();
        } catch {
          showToast({ msg: "Failed to create context. Please try again.", type: "error", duration: 4000 });
          return;
        }
        const created = (Array.isArray(newRecord) ? newRecord[0] : newRecord) as unknown as BoardContext;
        setResolvedContextId(created.id);
        onContextCreated?.(created.id);
        existing = (store.getById(created.id) as unknown as BoardContext) ?? null;
      }
      if (!existing) return;

      let extendDuplicateId: number | null = null;

      if (mode === "extend" && existing.isActive && duplicateContextForExtend) {
        try {
          const newId = await duplicateContextForExtend(existing.id);
          existing = (store.getById(newId) as unknown as BoardContext) ?? null;
          extendDuplicateId = newId;
          if (!existing) return;
        } catch {
          showToast({ msg: "Failed to duplicate context. Please try again.", type: "error", duration: 4000 });
          return;
        }
      }

      const maybeFinalizeExtend = () => {
        if (extendDuplicateId != null) {
          finalizeExtendDuplicate?.(extendDuplicateId);
          extendDuplicateId = null;
        }
      };

      const patch: Record<string, any> = {
        ...(extendDuplicateId == null && mode !== "extend" ? { name: name.trim() || null } : {}),
        websiteUrl: websiteUrl || null,
        businessDescription: businessDescription || null,
        ...(privateDocumentId !== null ? { privateDocument: privateDocumentId } : {}),
      };
      existing.set(patch);

      try {
        await project.sync();
      } catch {
        showToast({ msg: "Failed to save context sources.", type: "error", duration: 4000 });
        return;
      }

      if (websiteUrl) {
        setIsLoadingMapUrl(true);
        try {
          const res = await api.mapUrl(websiteUrl);
          const links: CrawledPage[] = res.data.links ?? [];
          maybeFinalizeExtend();
          setCrawledPages(links);
          setSelectedUrls(defaultSelectedUrls(links));
          setStepOverride("page-picker");
        } catch {
          showToast({ msg: "Could not crawl the URL. Proceeding with description/document.", type: "error", duration: 4000 });
          maybeFinalizeExtend();
          await triggerGenerateQuestions(existing.id);
        } finally {
          setIsLoadingMapUrl(false);
        }
      } else {
        maybeFinalizeExtend();
        await triggerGenerateQuestions(existing.id);
      }
    },
    [project, api, boardId, getContextRecord, draftNewContext, mode, onContextCreated, duplicateContextForExtend, finalizeExtendDuplicate, triggerGenerateQuestions]
  );

  const handleAnalysePages = useCallback(async () => {
    if (!project) return;
    setIsSubmittingPagePick(true);
    try {
      const existing = getContextRecord();
      if (existing) {
        existing.selectedUrls = selectedUrls;
        await project.sync();
      }
      await triggerGenerateQuestions();
    } finally {
      setIsSubmittingPagePick(false);
    }
  }, [project, getContextRecord, selectedUrls, triggerGenerateQuestions]);

  const handleBuildContext = useCallback(
    async (withoutAnswers = false) => {
      if (!api || !boardContextRecord?.draftQuestions?.questions) return;
      if (withoutAnswers) setAnswers({});

      setStepOverride(null);
      setIsSubmittingAnswers(true);

      const questions = boardContextRecord.draftQuestions.questions.map((q, i) => ({
        text: q.text,
        question_type: q.question_type,
        options: q.options,
        answer: withoutAnswers ? [] : (answers[i] ?? []),
      }));

      try {
        const res = await api.generateContext(boardContextRecord.id, questions);
        const taskId = String(res.data.id);
        pinContextTask(
          boardContextRecord.id,
          ASYNC_TASK_TYPES.BOARD_CONTEXT_GENERATION,
          taskId,
        );
      } catch (error) {
        reportError({
          error,
          provider: "openai",
          fallback: "Failed to start context generation.",
        });
      } finally {
        setIsSubmittingAnswers(false);
      }
    },
    [api, boardContextRecord, answers, pinContextTask, reportError]
  );

  const handleGenerateSuggestions = useCallback(async () => {
    const record = getContextRecord();
    if (!api || isGeneratingSuggestions || !record?.id) return;
    if (record.isActive) {
      showToast({ msg: "Use Duplicate & generate to update an active context.", type: "error", duration: 4000 });
      return;
    }
    setStepOverride(null);
    setIsGeneratingSuggestions(true);
    try {
      const res = await api.suggest(record.id);
      const taskId = String(res.data.id);
      pinContextTask(
        record.id,
        ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION,
        taskId,
      );
    } catch (error) {
      reportError({
        error,
        provider: "openai",
        fallback: "Failed to start strategic suggestions.",
      });
    } finally {
      setIsGeneratingSuggestions(false);
    }
  }, [api, getContextRecord, isGeneratingSuggestions, pinContextTask, reportError]);

  const handleDuplicateAndGenerate = useCallback(async () => {
    const record = getContextRecord();
    if (!api || !record?.id) return;

    let targetId = record.id;
    if (record.isActive && duplicateContextForExtend) {
      try {
        targetId = await duplicateContextForExtend(record.id);
        finalizeExtendDuplicate?.(targetId);
      } catch {
        showToast({ msg: "Failed to duplicate context.", type: "error", duration: 4000 });
        return;
      }
    }

    setStepOverride(null);
    setIsGeneratingSuggestions(true);
    try {
      const res = await api.suggest(targetId);
      const taskId = String(res.data.id);
      pinContextTask(
        targetId,
        ASYNC_TASK_TYPES.BOARD_CONTEXT_SUGGESTION,
        taskId,
      );
    } catch (error) {
      reportError({
        error,
        provider: "openai",
        fallback: "Failed to start strategic suggestions.",
      });
    } finally {
      setIsGeneratingSuggestions(false);
    }
  }, [api, getContextRecord, duplicateContextForExtend, finalizeExtendDuplicate, pinContextTask, reportError]);

  // ── Navigation helpers ────────────────────────────────────────────────────

  const handleBackFromPagePicker = useCallback(() => {
    setCrawledPages([]);
    setSelectedUrls([]);
    setStepOverride("setup");
  }, []);

  const handleBackFromQuestions = useCallback(() => {
    setAnswers({});
    const hasSavedPages = (boardContextRecord?.selectedUrls ?? []).length > 0;
    if (hasSavedPages) {
      setSelectedUrls(boardContextRecord?.selectedUrls ?? []);
      setStepOverride("page-picker");
    } else {
      setStepOverride("setup");
    }
  }, [boardContextRecord]);

  const handleContinueToQuestions = useCallback(() => {
    setStepOverride(null);
  }, []);

  // Derived
  const selectedUrlsMatchSaved = useMemo(() => {
    const saved = boardContextRecord?.selectedUrls ?? [];
    return selectedUrlsEqual(selectedUrls, saved);
  }, [selectedUrls, boardContextRecord]);

  const canContinueToQuestions =
    boardContextRecord?.status === "in_progress" &&
    hasDraftQuestionsFlag &&
    selectedUrlsMatchSaved &&
    selectedUrls.length > 0;

  const value: ContextFormContextValue = useMemo(
    () => ({
      contextId: effectiveContextId,
      boardContextRecord,
      groups,
      statements,
      draftQuestions,
      currentStep,
      hasContextView,
      hasDraftQuestions: hasDraftQuestionsFlag,
      isLoadingMapUrl,
      crawledPages,
      handleSetupSubmit,
      selectedUrls,
      setSelectedUrls,
      handleAnalysePages,
      isSubmittingPagePick,
      canContinueToQuestions,
      handleContinueToQuestions,
      answers,
      setAnswer,
      handleBuildContext,
      isSubmittingAnswers,
      isGeneratingSuggestions,
      handleGenerateSuggestions,
      handleDuplicateAndGenerate,
      handleBackFromPagePicker,
      handleBackFromQuestions,
      canCancelDraft: draftNewContext && resolvedContextId == null && !!onDraftCancel,
      cancelDraft: onDraftCancel ?? (() => {}),
      hasOpenAIKey,
      panelMode,
    }),
    [
      effectiveContextId,
      boardContextRecord,
      groups,
      statements,
      draftQuestions,
      currentStep,
      hasContextView,
      hasDraftQuestionsFlag,
      isLoadingMapUrl,
      crawledPages,
      handleSetupSubmit,
      selectedUrls,
      handleAnalysePages,
      isSubmittingPagePick,
      canContinueToQuestions,
      handleContinueToQuestions,
      answers,
      setAnswer,
      handleBuildContext,
      isSubmittingAnswers,
      isGeneratingSuggestions,
      handleGenerateSuggestions,
      handleDuplicateAndGenerate,
      handleBackFromPagePicker,
      handleBackFromQuestions,
      draftNewContext,
      resolvedContextId,
      onDraftCancel,
      hasOpenAIKey,
      panelMode,
    ]
  );

  return (
    <ContextFormCtx.Provider value={value}>
      {children}
    </ContextFormCtx.Provider>
  );
};

export const useContextForm = () => {
  const ctx = useContext(ContextFormCtx);
  if (!ctx) throw new Error("useContextForm must be used within ContextFormProvider");
  return ctx;
};
